import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { X } from "lucide-react";
import { useLanguage } from "@/components/language-provider";
import { Mascot, mascotFrameUrl } from "./mascot";
import {
  GUIDE_REQUEST_EVENT, GUIDE_STATE_EVENT, GuideRequest, MASCOT_JUMP_EVENT, MASCOT_LANDED_EVENT, findGuideTarget, isVisible, mascotHomeRect,
} from "./guide";
import "./assistant.css";

const SIZE = 96;
const GAP = 14;
const TIMEOUT_MS = 18_000;
const FIND_MS = 4_000;

interface Pointing {
  target: HTMLElement;
  title: string;
  rect: DOMRect;
}

/**
 * The mascot leaves the chat, flies to a real button, stands beside it pointing with a speech bubble, and flies back
 * when the button is clicked (or the bubble closed, Esc, or after a while). Also plays the "jump into the chat"
 * used by the onboarding. Mounted once, next to the chat widget.
 */
export function MascotGuide() {
  const { language } = useLanguage();
  const [location, navigate] = useLocation();
  const [pointing, setPointing] = useState<Pointing | null>(null);
  const [visible, setVisible] = useState(false);
  const flyer = useRef<HTMLImageElement>(null);
  const pending = useRef<GuideRequest | null>(null);
  const timeout = useRef<number | undefined>(undefined);
  const pointingRef = useRef<Pointing | null>(null);
  pointingRef.current = pointing;

  const broadcast = (active: boolean, rect?: DOMRect) =>
    window.dispatchEvent(new CustomEvent(GUIDE_STATE_EVENT, { detail: { active, rect } }));

  /** Flies a mascot image from one rect to another (Web Animations); resolves when it lands. */
  const fly = useCallback((from: DOMRect, to: DOMRect, frame = "point_a"): Promise<void> => {
    const img = flyer.current;
    if (!img || typeof img.animate !== "function") return Promise.resolve();
    img.src = mascotFrameUrl(frame);
    img.style.display = "block";
    const fromScale = Math.max(0.3, from.width / SIZE);
    const toScale = Math.max(0.3, to.width / SIZE);
    const midY = Math.min(from.top, to.top) - 90;
    const anim = img.animate([
      { transform: `translate(${from.left}px, ${from.top}px) scale(${fromScale})`, opacity: 1, offset: 0 },
      { transform: `translate(${(from.left + to.left) / 2}px, ${midY}px) scale(${(fromScale + toScale) / 2}) rotate(-8deg)`, opacity: 1, offset: 0.55 },
      { transform: `translate(${to.left}px, ${to.top}px) scale(${toScale})`, opacity: 1, offset: 1 },
    ], { duration: 720, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "forwards" });
    return new Promise((resolve) => {
      anim.onfinish = () => { img.style.display = "none"; resolve(); };
      anim.oncancel = () => { img.style.display = "none"; resolve(); };
    });
  }, []);

  const place = useCallback((target: HTMLElement) => target.getBoundingClientRect(), []);

  /** Where the mascot stands relative to the target: left of it when there is room, else right, else below. */
  const layout = (rect: DOMRect) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (rect.left - GAP - SIZE >= 8) return { left: rect.left - GAP - SIZE, top: clamp(rect.top + rect.height / 2 - SIZE / 2, 8, vh - SIZE - 8), flip: false, bubbleSide: "left" as const };
    if (rect.right + GAP + SIZE <= vw - 8) return { left: rect.right + GAP, top: clamp(rect.top + rect.height / 2 - SIZE / 2, 8, vh - SIZE - 8), flip: true, bubbleSide: "right" as const };
    return { left: clamp(rect.left + rect.width / 2 - SIZE / 2, 8, vw - SIZE - 8), top: Math.min(rect.bottom + GAP, vh - SIZE - 8), flip: false, bubbleSide: "below" as const, up: true };
  };

  const finish = useCallback(async (reason: "click" | "close" | "timeout" | "nav") => {
    const p = pointingRef.current;
    if (!p) return;
    window.clearTimeout(timeout.current);
    setVisible(false);
    setPointing(null);
    const home = mascotHomeRect();
    const pos = layout(p.rect);
    if (home && reason !== "nav") {
      await fly(new DOMRect(pos.left, pos.top, SIZE, SIZE), home, reason === "click" ? "happy_a" : "idle_a");
    }
    broadcast(false);
    window.dispatchEvent(new CustomEvent(MASCOT_LANDED_EVENT, { detail: { reason } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fly]);

  const start = useCallback(async (target: HTMLElement, title: string) => {
    const rect = place(target);
    const pos = layout(rect);
    broadcast(true, rect);
    const home = mascotHomeRect() ?? new DOMRect(window.innerWidth - 80, window.innerHeight - 80, 56, 56);
    target.scrollIntoView({ block: "nearest", inline: "nearest" });
    await fly(home, new DOMRect(pos.left, pos.top, SIZE, SIZE));
    setPointing({ target, title, rect: place(target) });
    setVisible(true);
    window.clearTimeout(timeout.current);
    timeout.current = window.setTimeout(() => finish("timeout"), TIMEOUT_MS);
  }, [fly, place, finish]);

  // Requests from the chat (or anything else).
  useEffect(() => {
    const onRequest = (e: Event) => {
      const req = (e as CustomEvent<GuideRequest>).detail;
      if (!req?.anchor) return;
      const el = findGuideTarget(req);
      if (el) { void start(el, req.title); return; }
      if (req.path && req.path !== location) {
        pending.current = req;
        navigate(req.path);
        return;
      }
      // Give the screen a moment (menus render after data arrives), then try once more.
      pending.current = req;
      const until = Date.now() + FIND_MS;
      const tick = () => {
        if (pending.current !== req) return;
        const found = findGuideTarget(req);
        if (found) { pending.current = null; void start(found, req.title); return; }
        if (Date.now() < until) window.setTimeout(tick, 150);
        else pending.current = null;
      };
      window.setTimeout(tick, 150);
    };
    window.addEventListener(GUIDE_REQUEST_EVENT, onRequest);
    return () => window.removeEventListener(GUIDE_REQUEST_EVENT, onRequest);
  }, [location, navigate, start]);

  // After a navigation requested by the guide, look for the anchor on the new screen.
  useEffect(() => {
    const req = pending.current;
    if (!req) { if (pointingRef.current) void finish("nav"); return; }
    const until = Date.now() + FIND_MS;
    const tick = () => {
      if (pending.current !== req) return;
      const found = findGuideTarget(req);
      if (found) { pending.current = null; void start(found, req.title); return; }
      if (Date.now() < until) window.setTimeout(tick, 150);
      else pending.current = null;
    };
    window.setTimeout(tick, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Follow the target while scrolling/resizing; end on click, Esc.
  useEffect(() => {
    if (!pointing) return;
    const update = () => {
      const p = pointingRef.current;
      if (!p) return;
      if (!isVisible(p.target)) { void finish("nav"); return; }
      setPointing({ ...p, rect: place(p.target) });
    };
    const onClick = (e: MouseEvent) => {
      const p = pointingRef.current;
      if (p && e.target instanceof Node && p.target.contains(e.target)) void finish("click");
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") void finish("close"); };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKey);
    const interval = window.setInterval(update, 500);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKey);
      window.clearInterval(interval);
    };
  }, [pointing?.target, finish, place]); // eslint-disable-line react-hooks/exhaustive-deps

  // Onboarding: jump into the chat.
  useEffect(() => {
    const onJump = async (e: Event) => {
      const from = (e as CustomEvent<{ from: DOMRect }>).detail?.from;
      const home = mascotHomeRect();
      if (!from || !home) return;
      await fly(from, home, "happy_a");
      window.dispatchEvent(new CustomEvent(MASCOT_LANDED_EVENT, { detail: { reason: "jump" } }));
    };
    window.addEventListener(MASCOT_JUMP_EVENT, onJump);
    return () => window.removeEventListener(MASCOT_JUMP_EVENT, onJump);
  }, [fly]);

  const pos = pointing ? layout(pointing.rect) : null;
  const text = pointing
    ? (language === "pt" ? `É aqui! Clique em “${pointing.title}”.` : `Here! Click “${pointing.title}”.`)
    : "";

  return (
    <>
      <img ref={flyer} alt="" className="fs-guide-flyer" style={{ display: "none", width: SIZE, height: SIZE }} draggable={false} />
      {pointing && pos && (
        <div className="fs-guide" data-testid="mascot-guide" style={{ opacity: visible ? 1 : 0 }}>
          <div
            className="fs-guide-ring"
            style={{ left: pointing.rect.left - 6, top: pointing.rect.top - 6, width: pointing.rect.width + 12, height: pointing.rect.height + 12 }}
          />
          <div className="fs-guide-mascot" style={{ left: pos.left, top: pos.top, width: SIZE, height: SIZE }}>
            <Mascot mood={pos.up ? "point-up" : "point"} size={SIZE} flip={pos.flip} />
          </div>
          <div
            className={`fs-guide-bubble fs-guide-bubble-${pos.bubbleSide}`}
            style={bubbleStyle(pos, pointing.rect)}
            data-testid="mascot-guide-bubble"
          >
            <span>{text}</span>
            <button type="button" onClick={() => finish("close")} aria-label={language === "pt" ? "Fechar" : "Close"} className="fs-guide-close">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function bubbleStyle(pos: { left: number; top: number; bubbleSide: "left" | "right" | "below" }, rect: DOMRect): React.CSSProperties {
  const vw = window.innerWidth;
  if (pos.bubbleSide === "below") return { left: clamp(pos.left - 60, 8, vw - 260), top: pos.top + SIZE + 6 };
  // Above the mascot, aligned to its side.
  const left = pos.bubbleSide === "left" ? clamp(pos.left - 120, 8, vw - 260) : clamp(pos.left, 8, vw - 260);
  return { left, top: Math.max(8, pos.top - 54) };
}
